/**
 * Auth session context.
 *
 * Storage: localStorage key `@AuthData`, JSON `{ user, token }` — the exact
 * shape the mobile app persists, so the Apollo authLink and upload service can
 * read the token without going through React.
 *
 * The session is an external store read with `useSyncExternalStore` (web plan
 * W4.2, `@/lib/storageStore`): the server, which has no storage, renders a
 * guest, and so does the render that hydrates its HTML; the stored user
 * follows in the render right after. In a tree mounted with `createRoot` (the
 * SPA) the first render already has the stored session — localStorage is
 * synchronous, so there is no flash of logged-out UI. `loading` is kept (always
 * false) for API symmetry with the mobile AuthContext. The header's account
 * button is a fixed 36 × 36 px slot either way (Header), so the switch after
 * hydration moves nothing.
 *
 * Guest mode: user/token are null; browsing works; auth unlocks favorites,
 * follows, notifications and settings.
 */
import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  useSyncExternalStore,
} from 'react';
import { AUTH_STORAGE_KEY } from '@/constants/config';
import { clearApolloStorage } from '@/lib/apollo';
import { createStorageStore, type StorageStore } from '@/lib/storageStore';

export interface User {
  id: string;
  fullName: string;
  userName: string;
  email: string;
  avatar?: string;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  signIn: (user: User, token: string) => Promise<void>;
  signOut: () => Promise<void>;
  /** Merge fields into the current user (e.g. after updating the avatar). */
  updateUser: (patch: Partial<User>) => Promise<void>;
  /** Always false on web (synchronous hydration) — kept for API symmetry. */
  loading: boolean;
}

interface StoredAuth {
  user: User | null;
  token: string | null;
}

/** The server's session, and the hydration render's: nobody is signed in. */
const GUEST: StoredAuth = Object.freeze({ user: null, token: null });

function readStoredAuth(): StoredAuth {
  try {
    const raw = window.localStorage.getItem(AUTH_STORAGE_KEY);
    if (!raw) return { user: null, token: null };
    const parsed = JSON.parse(raw) as { user?: User | null; token?: string | null };
    return { user: parsed.user ?? null, token: parsed.token ?? null };
  } catch {
    return { user: null, token: null };
  }
}

function persistAuth(user: User | null, token: string | null): void {
  try {
    if (user && token) {
      window.localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify({ user, token }));
    } else {
      window.localStorage.removeItem(AUTH_STORAGE_KEY);
    }
  } catch {
    // Storage full / blocked — session still works for this tab.
  }
}

/**
 * One store per provider (the app has one): it reads storage on the first
 * browser render, and its `storage` listener keeps tabs in sync — a login or
 * logout in another tab updates this one too.
 */
function createAuthStore(): StorageStore<StoredAuth> {
  return createStorageStore<StoredAuth>({
    key: AUTH_STORAGE_KEY,
    read: readStoredAuth,
    write: ({ user, token }) => persistAuth(user, token),
    serverSnapshot: GUEST,
  });
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [store] = useState(createAuthStore);
  const auth = useSyncExternalStore(store.subscribe, store.getSnapshot, store.getServerSnapshot);

  const signIn = useCallback(
    async (newUser: User, newToken: string) => {
      store.set({ user: newUser, token: newToken });
    },
    [store],
  );

  const signOut = useCallback(async () => {
    store.set({ user: null, token: null });
    // Wipe the Apollo cache and its localStorage persistence so this user's
    // notifications/favorites never leak into the next session.
    await clearApolloStorage();
  }, [store]);

  const updateUser = useCallback(
    async (patch: Partial<User>) => {
      store.set((prev) => (prev.user ? { user: { ...prev.user, ...patch }, token: prev.token } : prev));
    },
    [store],
  );

  const value = useMemo<AuthContextType>(
    () => ({
      user: auth.user,
      token: auth.token,
      signIn,
      signOut,
      updateUser,
      loading: false,
    }),
    [auth.user, auth.token, signIn, signOut, updateUser],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
